import React from 'react';
import { Heart } from 'lucide-react';

const titleOptions = [
  { value: 'brud', label: 'Bride' },
  { value: 'brudgom', label: 'Groom' },
  { value: 'partner', label: 'Partner' },
  { value: 'custom', label: 'Egendefinert' },
];

const PersonCard = ({ num, name, title, customTitle, onChange }) => (
  <div className="flex-1 bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
    <p className="text-[10px] font-sans font-bold text-stone-400 uppercase tracking-widest">Person {num}</p>
    <div>
      <label className="text-xs font-sans font-medium text-stone-600 mb-1.5 block">Navn</label>
      <input data-testid={`couple-person${num}-name`} value={name || ''} onChange={e => onChange(`person${num}Name`, e.target.value)}
        placeholder={num === 1 ? 'e.g. Anna' : 'e.g. Erik'}
        className="w-full text-sm font-sans px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:bg-white focus:outline-none transition-colors" />
    </div>
    <div>
      <label className="text-xs font-sans font-medium text-stone-600 mb-2 block">Betegnelse</label>
      <div className="grid grid-cols-2 gap-1.5">
        {titleOptions.map(opt => (
          <button key={opt.value} type="button" data-testid={`couple-person${num}-title-${opt.value}`}
            onClick={() => onChange(`person${num}Title`, opt.value)}
            className={`text-xs font-sans px-3 py-2 rounded-xl border transition-all ${
              title === opt.value ? 'bg-primary/10 border-primary text-primary font-semibold' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-400'
            }`}>{opt.label}</button>
        ))}
      </div>
    </div>
    {title === 'custom' && (
      <div>
        <label className="text-xs font-sans font-medium text-stone-600 mb-1.5 block">Din betegnelse</label>
        <input data-testid={`couple-person${num}-custom`} value={customTitle || ''} onChange={e => onChange(`person${num}CustomTitle`, e.target.value)}
          placeholder="e.g. Groom, Medgifter, Elskling"
          className="w-full text-sm font-sans px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:bg-white focus:outline-none" />
      </div>
    )}
  </div>
);

export const getPersonTitle = (formData, num) => {
  const title = formData[`person${num}Title`];
  if (title === 'custom') return formData[`person${num}CustomTitle`] || `Person ${num}`;
  return titleOptions.find(o => o.value === title)?.label || `Person ${num}`;
};

export const getCoupleLabel = (formData) => {
  const n1 = formData.person1Name || 'Person 1';
  const n2 = formData.person2Name || 'Person 2';
  return `${n1} & ${n2}`;
};

export const getSideLabel = (formData, side) => {
  if (side === 'both') return 'Felles';
  const num = side === 'person1' ? 1 : 2;
  const name = formData[`person${num}Name`];
  return name ? `${name}s side` : `Person ${num}s side`;
};

export const CoupleSetup = ({ formData, onChange }) => {
  const p1Name = formData.person1Name;
  const p2Name = formData.person2Name;
  const p1Title = getPersonTitle(formData, 1);
  const p2Title = getPersonTitle(formData, 2);
  const hasPreview = p1Name?.trim() || p2Name?.trim();

  return (
    <div data-testid="couple-setup" className="animate-fade-in-up space-y-6">
      <div className="text-center mb-2">
        <h2 className="text-xl font-serif font-bold text-stone-900 mb-1">Hvem er dere?</h2>
        <p className="text-xs font-sans text-stone-500 max-w-md mx-auto">
          Tell us a bit about yourselves. The rest of the planner is automatically customized based on what you fill in here.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-stretch">
        <PersonCard num={1} name={formData.person1Name} title={formData.person1Title} customTitle={formData.person1CustomTitle} onChange={onChange} />
        <div className="flex items-center justify-center md:py-8">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Heart className="w-5 h-5 text-primary" />
          </div>
        </div>
        <PersonCard num={2} name={formData.person2Name} title={formData.person2Title} customTitle={formData.person2CustomTitle} onChange={onChange} />
      </div>

      {hasPreview && (
        <div className="text-center bg-stone-50 rounded-2xl border border-stone-100 py-4 px-6">
          <p className="text-[10px] font-sans font-bold text-stone-400 uppercase tracking-widest mb-1">Preview</p>
          <p className="text-lg font-serif font-bold text-stone-800">
            {p1Name || 'Person 1'} <span className="text-primary">&</span> {p2Name || 'Person 2'}
          </p>
          <p className="text-xs font-sans text-stone-500 mt-0.5">{p1Title} & {p2Title}</p>
        </div>
      )}
    </div>
  );
};
